import { supabase } from "../lib/supabase";

/**
 * Shared client side of the documents queue for the portal: request generation
 * of a rapport-bound PDF (herstelformulier, inspectiecertificaat, ...), wait
 * for the printer service to finish it, and download the result.
 *
 * The pipeline behind a request is asynchronous and can legitimately take
 * minutes: the DB trigger enqueues on pgmq, `process-document-queue` dispatches
 * to the printer with an exponential backoff (15s, 30s, 60s, 120s, ... on a
 * failed dispatch) and the printer itself renders at most
 * RENDER_QUEUE_CONCURRENCY jobs at a time, so a burst queues up. See
 * docs/DOCUMENT_GENERATION_FLOW.md.
 */

/** Total time we wait before handing the job off to the background. Covers the queue's first four dispatch retries (225s) plus a render. */
const DOCUMENT_WAIT_TIMEOUT_MS = 300000;
/** Poll quickly at first (most documents finish in seconds), then back off. */
const DOCUMENT_POLL_FAST_MS = 1500;
const DOCUMENT_POLL_SLOW_MS = 4000;
const DOCUMENT_POLL_FAST_WINDOW_MS = 30000;
/** After this long the wait is worth telling the user about. */
const DOCUMENT_SLOW_NOTICE_MS = 20000;
/** A request younger than this that is still in flight is joined instead of restarted. */
const IN_FLIGHT_REUSE_MS = 180000;

const IN_FLIGHT_STATUSES = ["pending", "processing", "html_only"];

function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * The document is not ready yet, but the queue is still working on it — this is
 * not a failure: the PDF finishes in the background and shows up under
 * /documenten. Kept distinct from real errors so callers can keep it out of
 * Sentry and phrase it as "duurt langer" instead of "mislukt".
 */
export class DocumentGenerationTimeoutError extends Error {
  readonly documentId: string;

  constructor(message: string, documentId: string) {
    super(message);
    this.name = "DocumentGenerationTimeoutError";
    this.documentId = documentId;
    // Keeps `instanceof` working when the class is transpiled down.
    Object.setPrototypeOf(this, DocumentGenerationTimeoutError.prototype);
  }
}

export function isDocumentGenerationTimeout(error: unknown): boolean {
  return error instanceof Error && error.name === "DocumentGenerationTimeoutError";
}

/**
 * An in-flight request for the same rapport/type/version, if it is recent
 * enough to be worth joining. Re-requesting resets the row to `pending` and
 * re-enqueues, which throws away the progress of a render that is already
 * running — so a double click or a page reload during generation would keep
 * restarting the job and could never finish.
 */
async function findInFlightDocument(type: string, version: string, rapportId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from("documents")
      .select("id, status, updated_at")
      .eq("rapport_id", rapportId)
      .eq("type", type)
      .eq("version", version)
      .maybeSingle();

    if (error || !data) return null;
    if (!IN_FLIGHT_STATUSES.includes(String(data.status))) return null;

    const updatedAt = data.updated_at ? new Date(String(data.updated_at)).getTime() : NaN;
    if (Number.isNaN(updatedAt) || Date.now() - updatedAt > IN_FLIGHT_REUSE_MS) return null;

    return String(data.id);
  } catch {
    // Best effort only — fall through to a fresh request.
    return null;
  }
}

/**
 * Ask the backend to generate a rapport document and return its document id.
 * `subject` is the noun used in user-facing messages, e.g. "het formulier".
 */
export async function requestDocumentGeneration(params: {
  type: string;
  rapportId: string;
  subject: string;
  version?: string;
}): Promise<string> {
  const version = params.version ?? "v1";

  const inFlight = await findInFlightDocument(params.type, version, params.rapportId);
  if (inFlight) return inFlight;

  const { data, error } = await supabase.rpc("upsert_document_generation_request", {
    p_type: params.type,
    p_version: version,
    p_rapport_id: params.rapportId,
  });

  if (error) throw error;
  if (!data || typeof data !== "string") {
    throw new Error(`Geen document-id ontvangen voor ${params.subject}`);
  }
  return data;
}

/**
 * Poll the document row until the PDF is ready. Throws a
 * DocumentGenerationTimeoutError when the wait budget runs out (the job keeps
 * running server-side) and a plain Error when generation actually failed.
 */
export async function waitForCompletedDocument(
  documentId: string,
  options: { subject: string; onSlow?: () => void },
): Promise<{ id: string; status: string; pdf_path: string }> {
  const startedAt = Date.now();
  const deadline = startedAt + DOCUMENT_WAIT_TIMEOUT_MS;
  let notifiedSlow = false;

  while (Date.now() < deadline) {
    const { data, error: documentError } = await supabase
      .from("documents")
      .select("id, status, pdf_path")
      .eq("id", documentId)
      .maybeSingle();

    if (documentError) throw documentError;
    if (!data) throw new Error("Document niet gevonden");

    if (data.status === "completed" && data.pdf_path) {
      return data as { id: string; status: string; pdf_path: string };
    }
    if (data.status === "error") {
      throw new Error(`Genereren van ${options.subject} is mislukt`);
    }

    const elapsed = Date.now() - startedAt;
    if (!notifiedSlow && elapsed >= DOCUMENT_SLOW_NOTICE_MS) {
      notifiedSlow = true;
      options.onSlow?.();
    }

    await sleep(elapsed < DOCUMENT_POLL_FAST_WINDOW_MS ? DOCUMENT_POLL_FAST_MS : DOCUMENT_POLL_SLOW_MS);
  }

  throw new DocumentGenerationTimeoutError(
    `${options.subject.charAt(0).toUpperCase()}${options.subject.slice(1)} wordt nog gegenereerd. Zodra het klaar is vind je het bij Documenten.`,
    documentId,
  );
}

/** Download a generated PDF from the generated-docs bucket under `fileName`. */
export async function downloadGeneratedPdf(pdfPath: string, fileName: string, subject: string): Promise<void> {
  const { data: signed, error: signError } = await supabase.storage
    .from("generated-docs")
    .createSignedUrl(pdfPath, 3600, { download: fileName });

  if (signError) throw signError;
  if (!signed?.signedUrl) throw new Error(`Geen downloadlink beschikbaar voor ${subject}`);

  const link = window.document.createElement("a");
  link.href = signed.signedUrl;
  link.download = fileName;
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
}
